/**
 * PolicyPageShell — shared layout for every WashPros policy page.
 *
 * Consumes a content object from policyContent.js and renders:
 *   - sticky header with WashPros wordmark + Back button
 *   - title, badge, effective date, version
 *   - WashPros Global Services LLC banner (DBA-aware)
 *   - all sections (paragraphs + bullets) in order
 *   - related-policies list (cross-links)
 *   - contact box (legal + general inbox + postal address)
 *
 * Every interactive element receives a unique data-testid keyed on the
 * policy id so the testing agent can target it deterministically.
 */
import React from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { useSafeBack } from "@/utils/safeBack";
import {
  ArrowLeft,
  Droplets,
  FileText,
  Mail,
  ShieldAlert,
  Wrench,
  ReceiptText,
  MessageCircle,
  Shield,
  Cookie,
  Archive,
  Star,
  Megaphone,
  Stamp,
  Repeat,
  Banknote,
  Bot,
  Camera,
  AlertTriangle,
  Gavel,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "./policyContent";
import { policyById } from "./policyVersionRegistry";
import { useTranslation } from "react-i18next";

const ICONS = {
  ShieldAlert,
  Wrench,
  ReceiptText,
  MessageCircle,
  Shield,
  Cookie,
  Archive,
  Star,
  Megaphone,
  Stamp,
  Repeat,
  Banknote,
  Bot,
  Camera,
  AlertTriangle,
  Gavel,
  FileText,
};

const SECTION_HEADER_CLS =
  "text-2xl font-bold text-white mb-4 flex items-center gap-2";

const PolicyPageShell = ({ content }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const safeBack = useSafeBack();
  const BadgeIcon = ICONS[content.badgeIcon] || FileText;
  const meta = policyById(content.id) || {
    version: COMPANY.version,
    effective_date: COMPANY.effective,
    draft_status: t("legal.pending_legal_counsel_review"),
    contact_email: COMPANY.legalEmail,
    previous_version: null,
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"
      data-testid={`${content.id}-policy-page`}
    >
      {/* Sticky header */}
      <header className="bg-slate-900/80 border-b border-white/10 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => safeBack("/legal")}
              className="text-slate-400 hover:text-white"
              data-testid={`${content.id}-policy-back`}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <Droplets className="w-6 h-6 text-cyan-400" />
              <span className="text-xl font-bold text-white">
                WashPros<sup className="text-[0.5em]">®</sup>
              </span>
            </div>
          </div>
          <Link
            to="/legal"
            className="hidden sm:inline-flex items-center text-cyan-400 hover:text-cyan-300 text-sm font-medium"
            data-testid={`${content.id}-policy-hub-link`}
          >
            Trust & Policy Center →
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Title block */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-4">
            <BadgeIcon className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">
              {content.badge}
            </span>
          </div>
          <h1
            className="text-4xl font-bold text-white mb-4"
            data-testid={`${content.id}-policy-title`}
          >
            {content.title}
          </h1>

          {/* Policy version metadata — required on every policy page
              until legal counsel signs off. Do NOT add a "Last reviewed
              by legal" line here; that's intentionally omitted. */}
          <div
            className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-300 mt-3 px-4 py-2 rounded-full bg-slate-800/60 border border-white/10"
            data-testid={`${content.id}-policy-version-meta`}
          >
            <span data-testid={`${content.id}-policy-version`}>
              Policy version <strong className="text-white">v{meta.version}</strong>
            </span>
            <span className="text-slate-600">·</span>
            <span data-testid={`${content.id}-policy-effective`}>
              Effective <strong className="text-white">{meta.effective_date}</strong>
            </span>
            <span className="text-slate-600">·</span>
            <span
              className="text-amber-300"
              data-testid={`${content.id}-policy-draft-status`}
            >
              Internal draft status: {meta.draft_status}
            </span>
            <span className="text-slate-600">·</span>
            <a
              href={`mailto:${meta.contact_email}`}
              className="text-cyan-400 hover:underline"
              data-testid={`${content.id}-policy-version-contact`}
            >
              {meta.contact_email}
            </a>
          </div>

          {meta.previous_version ? (
            <p
              className="text-slate-500 text-xs mt-2"
              data-testid={`${content.id}-policy-previous-version`}
            >
              Previous version: v{meta.previous_version}
            </p>
          ) : null}

          {content.summary && (
            <p className="text-slate-300 max-w-2xl mx-auto mt-6 leading-relaxed">
              {content.summary}
            </p>
          )}
        </div>

        {/* LLC + DBA banner */}
        <div className="bg-slate-800/50 rounded-xl p-6 mb-8 border border-white/10">
          <p
            className="text-white font-semibold text-lg"
            data-testid={`${content.id}-policy-llc`}
          >
            {COMPANY.legalName}
          </p>
          <p className="text-slate-300 mt-1">{COMPANY.address}</p>
          <p className="text-slate-400 text-sm mt-2">
            {COMPANY.legalEmail} · {COMPANY.supportEmail} · {COMPANY.website}
          </p>
          <p
            className="text-amber-400 text-sm font-medium mt-3"
            data-testid={`${content.id}-policy-dba-note`}
          >
            {COMPANY.dbaNote}
          </p>
        </div>

        {/* Body sections */}
        <div
          className="prose prose-invert prose-lg max-w-none space-y-10"
          data-testid={`${content.id}-policy-content`}
        >
          {content.sections.map((sec, idx) => {
            // Stable key derived from heading — sections never reorder,
            // but using the heading text makes the React reconciler
            // immune to future re-ordering and clears the
            // array-index-as-key lint finding.
            const sectionKey = `${content.id}-${(sec.heading || `s${idx}`)
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "")}`;
            return (
            <section key={sectionKey}>
              <h2 className={SECTION_HEADER_CLS}>
                <FileText className="w-6 h-6 text-cyan-400" />
                {sec.heading}
              </h2>
              {Array.isArray(sec.body) &&
                sec.body.map((p, i) => (
                  <p key={`${sectionKey}-body-${i}`} className="text-slate-300 mt-3">
                    {p}
                  </p>
                ))}
              {Array.isArray(sec.items) && sec.items.length > 0 && (
                <ul className="list-disc list-inside text-slate-300 mt-3 space-y-2">
                  {sec.items.map((it, i) => (
                    <li key={`${sectionKey}-item-${i}`}>{it}</li>
                  ))}
                </ul>
              )}
              {Array.isArray(sec.bodyAfter) &&
                sec.bodyAfter.map((p, i) => (
                  <p key={`${sectionKey}-after-${i}`} className="text-slate-300 mt-3">
                    {p}
                  </p>
                ))}
            </section>
            );
          })}

          {/* Related policies */}
          {Array.isArray(content.related) && content.related.length > 0 && (
            <section>
              <h2 className={SECTION_HEADER_CLS}>
                <FileText className="w-6 h-6 text-cyan-400" />
                Related Policies
              </h2>
              <ul
                className="list-disc list-inside text-slate-300 mt-3 space-y-2"
                data-testid={`${content.id}-policy-related`}
              >
                {content.related.map((r) => (
                  <li key={r.to}>
                    <Link
                      to={r.to}
                      className="text-cyan-400 hover:underline"
                      data-testid={`${content.id}-policy-link-${r.to
                        .replace(/[^a-z0-9]+/gi, "-")
                        .replace(/^-+|-+$/g, "")}`}
                    >
                      {r.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Contact box */}
          <section className="bg-slate-800/50 rounded-xl p-6 border border-white/10 not-prose">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-cyan-400" />
              <h3 className="text-lg font-semibold text-white">
                Contacting Us
              </h3>
            </div>
            <p className="text-slate-300 text-sm">
              Legal & policy questions:{" "}
              <a
                href={`mailto:${COMPANY.legalEmail}`}
                className="text-cyan-400 hover:underline"
                data-testid={`${content.id}-policy-email-legal`}
              >
                {COMPANY.legalEmail}
              </a>
            </p>
            <p className="text-slate-300 text-sm mt-1">
              General support:{" "}
              <a
                href={`mailto:${COMPANY.supportEmail}`}
                className="text-cyan-400 hover:underline"
                data-testid={`${content.id}-policy-email-support`}
              >
                {COMPANY.supportEmail}
              </a>
            </p>
            <p className="text-slate-300 text-sm mt-1">
              Postal: {COMPANY.legalName}, Attn: Legal, {COMPANY.address}
            </p>
            <p className="text-slate-500 text-xs mt-4">
              © {new Date().getFullYear()} {COMPANY.legalName}. All rights
              reserved. WashPros<sup>®</sup>, The Car Wash Concierge Company
              <sup>™</sup>, Wax &amp; Shine Headquarters<sup>™</sup>, and
              WashPros Fleet<sup>™</sup> are trademarks of {COMPANY.legalName}.
              WashPros is a registered fictitious name / DBA in the State of
              Florida.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

PolicyPageShell.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    badge: PropTypes.string,
    badgeIcon: PropTypes.string,
    summary: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        heading: PropTypes.string.isRequired,
        body: PropTypes.arrayOf(PropTypes.string),
        items: PropTypes.arrayOf(PropTypes.string),
        bodyAfter: PropTypes.arrayOf(PropTypes.string),
      }),
    ).isRequired,
    related: PropTypes.arrayOf(
      PropTypes.shape({
        to: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
};

export default PolicyPageShell;
